import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    addDependency, getDependencies, getTasks, removeDependency,
    type Task, type TaskDependency,
} from '../../../packages/api-client/pm-api';
import { getErrorMessage } from '../../../packages/api-client';
import { Link } from 'react-router-dom';
import { Link2, Lock } from 'lucide-react';

// ─── Dependencies ─────────────────────────────────────────────────────────────

type DepType = 'finish_to_start' | 'start_to_start' | 'finish_to_finish' | 'start_to_finish';

const DEP_TYPE_OPTIONS: Array<{ value: DepType; label: string }> = [
    { value: 'finish_to_start', label: 'Finish-to-Start (default)' },
    { value: 'start_to_start', label: 'Start-to-Start' },
    { value: 'finish_to_finish', label: 'Finish-to-Finish' },
    { value: 'start_to_finish', label: 'Start-to-Finish' },
];

/** canAdd gates the picker (tasks:create); canRemove the × (tasks:delete) — the backend's route gates. */
export function DependencyList({ taskId, projectID, canAdd = true, canRemove = true }: { taskId: string; projectID: string; canAdd?: boolean; canRemove?: boolean }) {
    const [deps, setDeps] = useState<TaskDependency[]>([]);
    const [adding, setAdding] = useState(false);
    const [pickedTaskId, setPickedTaskId] = useState('');
    const [depType, setDepType] = useState<DepType>('finish_to_start');
    const [candidates, setCandidates] = useState<Task[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        setLoadError(false);
        getDependencies(taskId).then(setDeps).catch(() => setLoadError(true));
    }, [taskId]);

    // Lazily fetch project tasks the first time the user opens the picker.
    async function openPicker() {
        setAdding(true);
        if (candidates.length === 0) {
            const resp = await getTasks(projectID, { limit: '200' });
            setCandidates(resp.data ?? []);
        }
    }

    async function handleAdd() {
        if (!pickedTaskId) return;
        setSubmitting(true);
        try {
            const d = await addDependency(taskId, pickedTaskId, depType);
            setDeps(prev => [...prev, d]);
            setPickedTaskId('');
            setDepType('finish_to_start');
            setAdding(false);
        } catch (err: unknown) {
            // Backend's cycle-detection 400 surfaces here; toast it cleanly
            // instead of a browser alert.
            toast.error(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    }

    const DEP_LABELS: Record<string, string> = { finish_to_start: 'FS', start_to_start: 'SS', finish_to_finish: 'FF', start_to_finish: 'SF' };

    // Don't show the current task or already-linked dependencies in the picker.
    const linkedIds = new Set(deps.filter(d => d.accessible !== false).map(d => d.depends_on));
    const pickable = candidates.filter(c => c.id !== taskId && !linkedIds.has(c.id));

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} title="Tasks that must finish (or start) before this one can — adds to a critical-path graph">
                    <Link2 size={16} aria-hidden /> Dependencies ({deps.length})
                </span>
                {!adding && canAdd && (
                    <button className="btn btn-primary btn-sm" onClick={openPicker} aria-label="Add a dependency">
                        + Add dependency
                    </button>
                )}
            </div>
            {loadError && <p role="alert" className="task-state-error">Dependencies couldn't be loaded.</p>}
            {!loadError && deps.length === 0 && !adding && (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', margin: 0, padding: 'var(--space-2) 0' }}>
                    {canAdd
                        ? <>No blocking dependencies. Use <strong>+ Add dependency</strong> to mark another task as a blocker (e.g. &ldquo;Finish-to-Start&rdquo; means this task waits until the other one is done).</>
                        : 'No blocking dependencies.'}
                </p>
            )}
            {deps.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)' }}>
                    <span title={d.dep_type} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', background: 'var(--color-border)', padding: '1px 6px', borderRadius: 99 }}>{DEP_LABELS[d.dep_type] ?? d.dep_type}</span>
                    {/* accessible=false: the viewer may not see the prerequisite.
                        Render nothing from the row but its type — no title, id or link. */}
                    {d.accessible === false
                        ? <span style={{ flex: 1, color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}><Lock size={13} aria-hidden /> Dependency details unavailable</span>
                        : <Link to={`/tasks/${d.depends_on}`} style={{ flex: 1 }}>{d.depends_on_title}</Link>}
                    {/* A hidden prerequisite's row can't be removed by this viewer (the API 404s it). */}
                    {d.accessible !== false && canRemove && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }} aria-label="Remove dependency" onClick={async () => {
                        // A blocker the viewer can't see answers 404 — surface it instead of an unhandled rejection.
                        try { await removeDependency(taskId, d.id); setDeps(prev => prev.filter(x => x.id !== d.id)); }
                        catch (err) { toast.error(getErrorMessage(err)); }
                    }}>×</button>
                    )}
                </div>
            ))}
            {adding && canAdd && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                    <select className="form-input" style={{ fontSize: 'var(--font-size-sm)' }} autoFocus
                        value={pickedTaskId} onChange={e => setPickedTaskId(e.target.value)}>
                        <option value="">Pick a task this one depends on…</option>
                        {pickable.map(c => (
                            <option key={c.id} value={c.id}>{c.title}</option>
                        ))}
                    </select>
                    <select className="form-input" style={{ fontSize: 'var(--font-size-sm)' }}
                        value={depType} onChange={e => setDepType(e.target.value as DepType)}>
                        {DEP_TYPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!pickedTaskId || submitting}>
                            {submitting ? 'Adding…' : 'Add'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setPickedTaskId(''); }}>Cancel</button>
                    </div>
                    {pickable.length === 0 && candidates.length > 0 && (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                            No other tasks in this project to depend on.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
